import React, { useState } from 'react';
import { createLogger } from '../utils/logger.js';

const log = createLogger('ExportHTMLButton');

/**
 * Export Button - Opens print-friendly view in new window
 * Uses browser's native print to PDF functionality
 */
const ExportHTMLButton = ({ v2Data, className = '' }) => {
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    if (!v2Data || !v2Data.analysis) {
      alert('Please wait for the analysis to complete before exporting.');
      return;
    }

    setIsExporting(true);

    try {
      const html = generatePrintHTML(v2Data);

      // Open in new window for printing
      const printWindow = window.open('', '_blank', 'width=1200,height=800');
      if (!printWindow) {
        alert('Please allow popups to export the report.');
        setIsExporting(false);
        return;
      }

      printWindow.document.write(html);
      printWindow.document.close();

      // Give time for Mermaid diagrams to render, then trigger print
      setTimeout(() => {
        printWindow.print();
        setIsExporting(false);
      }, 1500);

    } catch (error) {
      log.error('Error exporting:', error);
      setIsExporting(false);
      alert('Failed to export. Please try again.');
    }
  };

  if (!v2Data?.analysis) return null;

  return (
    <div className={`fixed bottom-24 md:bottom-8 right-4 md:right-8 z-40 ${className}`}>
      <button
        onClick={handleExport}
        disabled={isExporting}
        className={`
          flex items-center justify-center gap-2 px-4 py-3 rounded-full
          bg-[#7B61FF] hover:bg-[#6b51eb] text-white font-medium
          shadow-[0_0_30px_rgba(123,97,255,0.4)] hover:shadow-[0_0_40px_rgba(123,97,255,0.6)]
          transition-all duration-300 transform hover:-translate-y-1
          disabled:opacity-70 disabled:cursor-wait disabled:transform-none
        `}
      >
        {isExporting ? (
          <>
            <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
            <span className="text-sm">Preparing...</span>
          </>
        ) : (
          <>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            <span className="hidden sm:inline">Export PDF</span>
          </>
        )}
      </button>
    </div>
  );
};

/**
 * Generate comprehensive print-ready HTML
 */
function generatePrintHTML(v2Data) {
  const { originalIdea, platform, research, validation, answers, analysis, prompts, flowDiagram } = v2Data;

  const e = (text) => {
    if (!text) return '';
    return String(text).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  };

  const date = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  // Extract all the nested data
  const refinedHLD = analysis?.refinedHLD || {};
  const techArch = refinedHLD.technicalArchitecture || {};
  const businessReqs = refinedHLD.businessRequirements || {};
  const blueprint = analysis?.cursorBlueprint || {};
  const riskAssess = analysis?.riskAssessment || {};
  const projectMeta = refinedHLD.projectMetadata || {};

  // Tech stack from multiple sources
  const techStack = validation?.refinedArchitecture?.techStack || research?.techStack || {};

  // Confidence
  const confidence = analysis?.confidence || refinedHLD.confidenceFactors?.overallConfidence || 0.85;
  const confidencePercent = Math.round((typeof confidence === 'number' ? confidence : 0.85) * 100);
  const confidenceFactors = refinedHLD.confidenceFactors || {};
  const metadata = analysis?.metadata || {};
  const claudeConfidence = metadata.claudeConfidence ? Math.round(metadata.claudeConfidence * 100) : null;
  const consensusScore = metadata.consensusScore ? Math.round(metadata.consensusScore * 100) : null;

  // Core features
  const coreFeatures = businessReqs.coreFeatures || research?.coreFeatures || [];
  const userTypes = businessReqs.userTypes || [];
  const dataRequirements = businessReqs.dataRequirements || [];
  const integrationNeeds = businessReqs.integrationNeeds || [];

  // Validation
  const improvements = validation?.improvements || validation?.validation?.improvements || [];
  const validationResults = validation?.validationResults || {};
  const refinedArch = validation?.refinedArchitecture || {};
  const improvedArch = validation?.improvedArchitecture || {};
  const concerns = validation?.concerns || validation?.validation?.concerns || [];
  const betterAlternatives = validation?.betterFreeAlternatives || validation?.validation?.betterFreeAlternatives || [];
  const validationRecommendations = validation?.recommendations || validation?.validation?.recommendations || '';
  const isArchitectureValid = validation?.isValid ?? validation?.validation?.isValid ?? true;

  // Risks
  const technicalRisks = riskAssess.technicalRisks || [];
  const businessRisks = riskAssess.businessRisks || [];
  const timelineRisks = riskAssess.timelineRisks || [];
  const mitigations = riskAssess.mitigationStrategies || [];

  // Additional analysis data
  const developmentPhases = analysis?.developmentPhases || refinedHLD.developmentStrategy?.phases || [];
  const serviceRecommendations = analysis?.serviceRecommendations || {};
  const budgetAnalysis = analysis?.budgetAnalysis || {};
  const freeTierAnalysis = analysis?.freeTierAnalysis || {};
  const detectedRequirements = analysis?.detectedRequirements || [];

  // Prompts metadata
  const promptsMetadata = prompts?.metadata || {};
  const totalEstimatedTime = promptsMetadata.totalEstimatedTime || '';
  const criticalPath = promptsMetadata.criticalPath || [];

  // Prompts - normalize all formats
  let promptsList = [];
  if (prompts?.microPrompts && Array.isArray(prompts.microPrompts)) {
    promptsList = prompts.microPrompts;
  } else if (prompts?.data?.prompts) {
    const p = prompts.data.prompts;
    promptsList = Object.entries(p).map(([key, val]) => ({ id: key, ...(typeof val === 'object' ? val : { prompt: val }) }));
  } else if (prompts && typeof prompts === 'object') {
    const entries = Object.entries(prompts).filter(([k, v]) => v && typeof v === 'object' && (v.title || v.prompt));
    if (entries.length > 0) {
      promptsList = entries.map(([key, val]) => ({ id: key, ...val }));
    }
  }

  // Flow diagrams
  const diagrams = flowDiagram?.data || flowDiagram || {};

  // Helper to render lists
  const renderList = (items) => {
    if (!items || !Array.isArray(items) || items.length === 0) return '<p class="empty">No data available</p>';
    return '<ul>' + items.map(item => {
      const text = typeof item === 'string' ? item : (item?.name || item?.description || item?.title || item?.risk || JSON.stringify(item));
      return `<li>${e(text)}</li>`;
    }).join('') + '</ul>';
  };

  // Helper to render object as definition list
  const renderObject = (obj, excludeKeys = []) => {
    if (!obj || typeof obj !== 'object') return '';
    const entries = Object.entries(obj).filter(([k]) => !excludeKeys.includes(k) && !k.startsWith('_'));
    if (entries.length === 0) return '';
    return '<dl>' + entries.map(([key, value]) => {
      const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase());
      let val = '';
      if (Array.isArray(value)) {
        val = value.map(v => typeof v === 'string' ? v : JSON.stringify(v)).join(', ');
      } else if (typeof value === 'object' && value !== null) {
        val = JSON.stringify(value, null, 2);
      } else {
        val = String(value);
      }
      return `<dt>${e(label)}</dt><dd>${e(val)}</dd>`;
    }).join('') + '</dl>';
  };

  // Helper to format complex objects nicely
  const formatComplexData = (data, indent = 0) => {
    if (!data) return '';
    if (typeof data === 'string') return `<p>${e(data)}</p>`;
    if (Array.isArray(data)) {
      return '<ul class="formatted-list">' + data.map(item => {
        if (typeof item === 'string') {
          return `<li>${e(item)}</li>`;
        } else if (typeof item === 'object' && item !== null) {
          const name = item.name || item.title || '';
          const desc = item.description || item.purpose || item.methods?.join(', ') || '';
          const entity = item.entity || '';
          return `<li><strong>${e(name)}</strong>${entity ? ` (${e(entity)})` : ''}${desc ? `: ${e(desc)}` : ''}</li>`;
        }
        return `<li>${e(String(item))}</li>`;
      }).join('') + '</ul>';
    }
    if (typeof data === 'object') {
      let html = '<div class="formatted-object">';
      for (const [key, value] of Object.entries(data)) {
        if (key.startsWith('_')) continue;
        const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase());
        html += `<div class="formatted-row">`;
        html += `<span class="formatted-label">${e(label)}:</span>`;
        if (typeof value === 'string') {
          html += `<span class="formatted-value">${e(value)}</span>`;
        } else if (Array.isArray(value)) {
          if (value.every(v => typeof v === 'string')) {
            html += `<span class="formatted-value">${value.map(v => e(v)).join(', ')}</span>`;
          } else {
            html += formatComplexData(value);
          }
        } else if (typeof value === 'object' && value !== null) {
          html += formatComplexData(value);
        } else {
          html += `<span class="formatted-value">${e(String(value))}</span>`;
        }
        html += '</div>';
      }
      html += '</div>';
      return html;
    }
    return `<p>${e(String(data))}</p>`;
  };

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>${e(originalIdea?.slice(0, 50) || 'Architecture Report')} - The AI Architect</title>
  <script src="https://cdn.jsdelivr.net/npm/mermaid@10/dist/mermaid.min.js"></script>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; color: #E5E7EB; background: #0a0a0f; font-size: 14px; }
    .container { max-width: 900px; margin: 0 auto; padding: 40px 30px; }

    /* Cover */
    .cover { text-align: center; padding: 60px 20px; border-bottom: 1px solid rgba(255,255,255,0.1); margin-bottom: 40px; }
    .cover .logo { width: 70px; height: 70px; background: linear-gradient(135deg, #7B61FF, #BA87FF); border-radius: 16px; margin: 0 auto 24px; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 32px; font-weight: bold; box-shadow: 0 0 40px rgba(123, 97, 255, 0.4); }
    .cover h1 { font-size: 36px; color: #fff; margin-bottom: 8px; }
    .cover .subtitle { color: #7B61FF; font-size: 18px; margin-bottom: 24px; }
    .cover .idea { color: rgba(255,255,255,0.7); max-width: 600px; margin: 0 auto 24px; }
    .cover .badges { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; }
    .badge { background: rgba(123, 97, 255, 0.15); border: 1px solid rgba(123, 97, 255, 0.3); color: #BA87FF; padding: 8px 20px; border-radius: 24px; font-size: 13px; font-weight: 600; }
    .badge.muted { background: rgba(255,255,255,0.05); border-color: rgba(255,255,255,0.1); color: rgba(255,255,255,0.6); }

    /* Sections */
    .section { background: rgba(20, 23, 34, 0.6); border: 1px solid rgba(255,255,255,0.08); border-radius: 20px; padding: 28px; margin-bottom: 24px; page-break-inside: avoid; }
    .section-title { font-size: 22px; font-weight: 700; color: #fff; margin-bottom: 24px; padding-bottom: 16px; border-bottom: 2px solid #7B61FF; display: flex; align-items: center; gap: 12px; }
    .section-title .icon { width: 36px; height: 36px; background: rgba(123, 97, 255, 0.2); border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 18px; }
    .subsection { margin: 24px 0; }
    .subsection-title { font-size: 16px; font-weight: 600; color: #7B61FF; margin-bottom: 12px; }

    /* Stats */
    .stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 24px; }
    .stat { background: rgba(15, 17, 23, 0.8); border: 1px solid rgba(255,255,255,0.1); border-radius: 12px; padding: 18px; text-align: center; }
    .stat-value { font-size: 22px; font-weight: 700; color: #7B61FF; }
    .stat-label { font-size: 11px; color: rgba(255,255,255,0.5); text-transform: uppercase; margin-top: 6px; letter-spacing: 0.5px; }

    /* Progress bar */
    .progress { margin-bottom: 24px; }
    .progress-bar { height: 10px; background: rgba(255,255,255,0.1); border-radius: 5px; overflow: hidden; }
    .progress-fill { height: 100%; background: linear-gradient(90deg, #7B61FF, #BA87FF); border-radius: 5px; }
    .progress-label { display: flex; justify-content: space-between; font-size: 13px; color: rgba(255,255,255,0.6); margin-top: 8px; }

    /* Cards */
    .card { background: rgba(15, 17, 23, 0.6); border: 1px solid rgba(255,255,255,0.08); border-radius: 12px; padding: 18px; margin-bottom: 12px; }
    .card-title { font-weight: 600; color: #fff; margin-bottom: 10px; }
    .card-content { color: rgba(255,255,255,0.7); font-size: 13px; }

    /* Tech grid */
    .tech-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
    .tech-item { background: rgba(15, 17, 23, 0.6); border: 1px solid rgba(255,255,255,0.08); border-radius: 12px; padding: 16px; }
    .tech-category { font-size: 10px; color: #7B61FF; text-transform: uppercase; font-weight: 600; margin-bottom: 6px; letter-spacing: 0.5px; }
    .tech-name { font-size: 15px; font-weight: 600; color: #fff; }
    .tech-detail { font-size: 12px; color: rgba(255,255,255,0.5); margin-top: 4px; }

    /* Lists */
    ul { list-style: none; padding: 0; }
    ul li { position: relative; padding-left: 20px; margin-bottom: 10px; color: rgba(255,255,255,0.8); }
    ul li::before { content: ''; position: absolute; left: 0; top: 8px; width: 8px; height: 8px; background: #7B61FF; border-radius: 50%; }

    /* Definition lists */
    dl { display: grid; grid-template-columns: 180px 1fr; gap: 10px 16px; }
    dt { font-weight: 600; color: #7B61FF; }
    dd { color: rgba(255,255,255,0.7); word-break: break-word; }

    /* Tables */
    table { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
    th, td { padding: 12px 14px; text-align: left; border-bottom: 1px solid rgba(255,255,255,0.1); font-size: 13px; }
    th { background: rgba(123, 97, 255, 0.1); font-weight: 600; color: #fff; }
    td { color: rgba(255,255,255,0.7); }

    /* Prompts */
    .prompt { background: rgba(15, 17, 23, 0.6); border: 1px solid rgba(255,255,255,0.08); border-left: 4px solid #7B61FF; border-radius: 12px; padding: 18px; margin-bottom: 16px; page-break-inside: avoid; }
    .prompt-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
    .prompt-number { font-size: 12px; color: #7B61FF; font-weight: 600; }
    .prompt-meta { font-size: 11px; color: rgba(255,255,255,0.5); }
    .prompt-title { font-size: 16px; font-weight: 600; color: #fff; margin-bottom: 10px; }
    .prompt-goal { font-size: 13px; color: rgba(255,255,255,0.6); font-style: italic; margin-bottom: 12px; }
    .prompt-content { background: rgba(0,0,0,0.3); border: 1px solid rgba(255,255,255,0.1); padding: 14px; border-radius: 8px; font-size: 12px; color: rgba(255,255,255,0.8); white-space: pre-wrap; word-break: break-word; font-family: 'Monaco', 'Menlo', monospace; }
    .prompt-deliverables { margin-top: 14px; font-size: 13px; }
    .prompt-deliverables strong { color: #fff; }

    /* Risk */
    .risk-high { color: #f87171; }
    .risk-medium { color: #fbbf24; }
    .risk-low { color: #4ade80; }

    /* Footer */
    .footer { text-align: center; padding: 40px 20px; border-top: 1px solid rgba(255,255,255,0.1); margin-top: 40px; }
    .footer p { color: rgba(255,255,255,0.5); font-size: 13px; }
    .footer a { color: #7B61FF; text-decoration: none; }

    .empty { color: rgba(255,255,255,0.5); font-style: italic; }
    .two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }

    /* Formatted data styles */
    .formatted-object { margin: 8px 0; }
    .formatted-row { margin-bottom: 14px; padding-bottom: 14px; border-bottom: 1px solid rgba(255,255,255,0.08); }
    .formatted-row:last-child { border-bottom: none; margin-bottom: 0; padding-bottom: 0; }
    .formatted-label { font-weight: 600; color: #7B61FF; display: block; margin-bottom: 6px; font-size: 13px; }
    .formatted-value { color: rgba(255,255,255,0.8); display: block; font-size: 13px; line-height: 1.7; }
    .formatted-list { margin: 8px 0 8px 8px; }
    .formatted-list li { margin-bottom: 12px; padding-bottom: 12px; border-bottom: 1px dashed rgba(255,255,255,0.1); }
    .formatted-list li:last-child { border-bottom: none; }
    .formatted-list li strong { color: #fff; }
    .formatted-object .formatted-object { margin-left: 16px; padding-left: 16px; border-left: 2px solid rgba(123, 97, 255, 0.3); }

    /* Mermaid diagrams - dark theme matching app glass-panel design */
    .diagrams-section {
      background: linear-gradient(135deg, rgba(20, 23, 34, 0.95), rgba(15, 17, 23, 0.98));
      border: 1px solid rgba(255, 255, 255, 0.08);
      border-radius: 20px;
      padding: 32px;
      margin-bottom: 40px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
    }
    .diagrams-section .section-title {
      color: #fff;
      border-bottom: 2px solid #7B61FF;
      padding-bottom: 16px;
      margin-bottom: 24px;
    }
    .diagrams-section .section-title .icon {
      background: rgba(123, 97, 255, 0.2);
      color: #7B61FF;
    }
    .mermaid-diagram {
      background: rgba(15, 17, 23, 0.8);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 16px;
      padding: 24px;
      margin-bottom: 20px;
      overflow-x: auto;
    }
    .mermaid-diagram pre.mermaid {
      display: flex;
      justify-content: center;
      min-height: 250px;
      margin: 0;
      padding: 16px;
      background: transparent;
      border: none;
    }
    .diagram-label {
      font-weight: 600;
      color: #E5E7EB;
      margin-bottom: 16px;
      font-size: 18px;
      padding-bottom: 12px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    }

    /* Mermaid dark theme overrides */
    .mermaid svg { color: #E5E7EB !important; background: transparent !important; font-family: inherit !important; }
    .mermaid .label, .mermaid text, .mermaid .nodeLabel, .mermaid .edgeLabel { fill: #E5E7EB !important; color: #E5E7EB !important; font-size: 14px !important; }
    .mermaid .node rect, .mermaid .node circle, .mermaid .node path, .mermaid .flowchart-node { fill: #141722 !important; stroke: #7B61FF !important; stroke-width: 2px !important; }
    .mermaid .cluster rect { fill: #0F1117 !important; stroke: #3A3D46 !important; }
    .mermaid .edgePath path, .mermaid .flowchart-link { stroke: #7B61FF !important; stroke-width: 2px !important; }
    .mermaid .journey-section { fill: #141722 !important; }
    .mermaid .actor { fill: #141722 !important; stroke: #7B61FF !important; }
    .mermaid .messageLine0, .mermaid .messageLine1 { stroke: #7B61FF !important; }
    .mermaid .labelBox { fill: #141722 !important; stroke: #3A3D46 !important; }
    .mermaid .loopText, .mermaid .noteText { fill: #E5E7EB !important; }
    .mermaid .section-0, .mermaid .section-1, .mermaid .section-2, .mermaid .section-3 { fill: #141722 !important; }
    .mermaid .task { fill: #7B61FF !important; stroke: #7B61FF !important; }
    .mermaid .taskText { fill: #fff !important; }
    .mermaid .sectionTitle { fill: #E5E7EB !important; }

    @media print {
      * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
      body { font-size: 11px; background: #0a0a0f !important; color: #E5E7EB !important; }
      .container { padding: 20px; }
      .section { page-break-inside: avoid; background: rgba(20, 23, 34, 0.9) !important; }
      .cover { page-break-after: always; }
      .no-print { display: none; }
      .card, .stat, .tech-item, .prompt { background: rgba(15, 17, 23, 0.8) !important; }
      .section-title, .card-title, .prompt-title, .tech-name { color: #fff !important; }
      .diagrams-section { background: #141722 !important; page-break-inside: avoid; }
      .mermaid-diagram { background: #0F1117 !important; page-break-inside: avoid; }
      .diagram-label { color: #E5E7EB !important; }
      .mermaid svg { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    }
  </style>
</head>
<body>
  <div class="container">
    <!-- Cover -->
    <div class="cover">
      <div class="logo">A</div>
      <h1>Architecture Report</h1>
      <p class="subtitle">The AI Architect</p>
      <p class="idea">${e(originalIdea || 'Your Application Architecture')}</p>
      <div class="badges">
        ${platform ? `<span class="badge">${e(platform)}</span>` : ''}
        ${projectMeta.complexity ? `<span class="badge muted">${e(projectMeta.complexity)} complexity</span>` : ''}
        <span class="badge muted">${date}</span>
      </div>
    </div>

    <!-- Executive Summary -->
    <div class="section">
      <h2 class="section-title"><span class="icon">📊</span> Executive Summary</h2>

      <div class="progress">
        <div class="progress-bar"><div class="progress-fill" style="width: ${confidencePercent}%"></div></div>
        <div class="progress-label"><span>Architecture Confidence</span><span>${confidencePercent}%</span></div>
      </div>

      <div class="stats">
        <div class="stat"><div class="stat-value">${e(platform || 'Web')}</div><div class="stat-label">Platform</div></div>
        <div class="stat"><div class="stat-value">${e(research?.estimatedCost?.monthly || '$0')}</div><div class="stat-label">Monthly Cost</div></div>
        <div class="stat"><div class="stat-value">${e(projectMeta.complexity || 'Medium')}</div><div class="stat-label">Complexity</div></div>
        <div class="stat"><div class="stat-value">${e(projectMeta.timeline || research?.estimatedTimeframe || 'TBD')}</div><div class="stat-label">Timeline</div></div>
        ${claudeConfidence ? `<div class="stat"><div class="stat-value">${claudeConfidence}%</div><div class="stat-label">AI Confidence</div></div>` : ''}
        ${consensusScore ? `<div class="stat"><div class="stat-value">${consensusScore}%</div><div class="stat-label">Consensus</div></div>` : ''}
      </div>

      ${originalIdea ? `
      <div class="subsection">
        <div class="subsection-title">Project Vision</div>
        <div class="card"><div class="card-content">${e(originalIdea)}</div></div>
      </div>
      ` : ''}

      ${refinedHLD.executiveSummary ? `
      <div class="subsection">
        <div class="subsection-title">Summary</div>
        <div class="card"><div class="card-content">${e(refinedHLD.executiveSummary)}</div></div>
      </div>
      ` : ''}

      ${Object.keys(confidenceFactors).filter(k => k !== 'overallConfidence').length > 0 ? `
      <div class="subsection">
        <div class="subsection-title">Confidence Breakdown</div>
        <div class="card">${formatComplexData(Object.fromEntries(Object.entries(confidenceFactors).filter(([k]) => k !== 'overallConfidence')))}</div>
      </div>
      ` : ''}
    </div>

    <!-- User Clarifications / Q&A -->
    ${answers && Array.isArray(answers) && answers.length > 0 ? `
    <div class="section">
      <h2 class="section-title"><span class="icon">💬</span> User Clarifications</h2>
      <p style="color: rgba(255,255,255,0.6); margin-bottom: 20px; font-size: 13px;">Responses provided during the requirements gathering phase:</p>
      ${answers.map((answer, idx) => {
        const question = answer.question || answer.label || `Question ${idx + 1}`;
        const response = answer.answer || answer.value || answer.response || 'No response';
        return `
        <div class="card">
          <div class="card-title">${e(question)}</div>
          <div class="card-content">${e(response)}</div>
        </div>
        `;
      }).join('')}
    </div>
    ` : ''}

    <!-- Business Requirements -->
    ${(coreFeatures.length > 0 || userTypes.length > 0 || dataRequirements.length > 0) ? `
    <div class="section">
      <h2 class="section-title"><span class="icon">📋</span> Business Requirements</h2>

      ${coreFeatures.length > 0 ? `
      <div class="subsection">
        <div class="subsection-title">Core Features</div>
        ${renderList(coreFeatures)}
      </div>
      ` : ''}

      ${userTypes.length > 0 ? `
      <div class="subsection">
        <div class="subsection-title">User Types</div>
        ${renderList(userTypes)}
      </div>
      ` : ''}

      ${dataRequirements.length > 0 ? `
      <div class="subsection">
        <div class="subsection-title">Data Requirements</div>
        ${renderList(dataRequirements)}
      </div>
      ` : ''}

      ${integrationNeeds.length > 0 ? `
      <div class="subsection">
        <div class="subsection-title">Integration Needs</div>
        ${renderList(integrationNeeds)}
      </div>
      ` : ''}

      ${detectedRequirements.length > 0 ? `
      <div class="subsection">
        <div class="subsection-title">Detected Requirements</div>
        ${renderList(detectedRequirements)}
      </div>
      ` : ''}
    </div>
    ` : ''}

    <!-- Development Phases -->
    ${developmentPhases.length > 0 ? `
    <div class="section">
      <h2 class="section-title"><span class="icon">📅</span> Development Phases</h2>
      ${developmentPhases.map((phase, idx) => {
        const title = phase.title || phase.name || `Phase ${idx + 1}`;
        const description = phase.description || '';
        const duration = phase.duration || phase.timeline || '';
        const tasks = phase.tasks || phase.deliverables || [];
        return `
        <div class="card">
          <div class="card-title" style="display: flex; align-items: center; gap: 12px;">
            <span style="background: #7B61FF; color: white; width: 28px; height: 28px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 14px; font-weight: bold;">${idx + 1}</span>
            ${e(title)}
            ${duration ? `<span class="badge muted" style="font-size: 11px;">${e(duration)}</span>` : ''}
          </div>
          ${description ? `<div class="card-content">${e(description)}</div>` : ''}
          ${tasks.length > 0 ? `
          <div style="margin-top: 12px;">
            <strong style="font-size: 12px; color: rgba(255,255,255,0.6);">Tasks:</strong>
            ${renderList(tasks)}
          </div>
          ` : ''}
        </div>
        `;
      }).join('')}
    </div>
    ` : ''}

    <!-- Technology Stack -->
    <div class="section">
      <h2 class="section-title"><span class="icon">⚙️</span> Technology Stack</h2>

      <div class="tech-grid">
        ${techStack.frontend || techArch.frontendArchitecture ? `
        <div class="tech-item">
          <div class="tech-category">Frontend</div>
          <div class="tech-name">${e(typeof techStack.frontend === 'string' ? techStack.frontend : (techArch.frontendArchitecture?.framework || techArch.frontendArchitecture?.technology || 'React'))}</div>
          ${techArch.frontendArchitecture?.type ? `<div class="tech-detail">${e(techArch.frontendArchitecture.type)}</div>` : ''}
        </div>
        ` : ''}

        ${techStack.backend || techArch.backendArchitecture ? `
        <div class="tech-item">
          <div class="tech-category">Backend</div>
          <div class="tech-name">${e(typeof techStack.backend === 'string' ? techStack.backend : (techArch.backendArchitecture?.framework || techArch.backendArchitecture?.technology || 'Node.js'))}</div>
          ${techArch.backendArchitecture?.type ? `<div class="tech-detail">${e(techArch.backendArchitecture.type)}</div>` : ''}
        </div>
        ` : ''}

        ${techStack.database || techArch.databaseArchitecture ? `
        <div class="tech-item">
          <div class="tech-category">Database</div>
          <div class="tech-name">${e(typeof techStack.database === 'string' ? techStack.database : (techArch.databaseArchitecture?.provider || techArch.databaseArchitecture?.technology || 'PostgreSQL'))}</div>
          ${techArch.databaseArchitecture?.type ? `<div class="tech-detail">${e(techArch.databaseArchitecture.type)}</div>` : ''}
        </div>
        ` : ''}

        ${techStack.authentication || techArch.securityArchitecture ? `
        <div class="tech-item">
          <div class="tech-category">Authentication</div>
          <div class="tech-name">${e(typeof techStack.authentication === 'string' ? techStack.authentication : (techArch.securityArchitecture?.authentication || 'JWT'))}</div>
        </div>
        ` : ''}

        ${techStack.deployment ? `
        <div class="tech-item">
          <div class="tech-category">Deployment</div>
          <div class="tech-name">${e(techStack.deployment)}</div>
        </div>
        ` : ''}

        ${techStack.cdn ? `
        <div class="tech-item">
          <div class="tech-category">CDN</div>
          <div class="tech-name">${e(techStack.cdn)}</div>
        </div>
        ` : ''}

        ${techStack.storage ? `
        <div class="tech-item">
          <div class="tech-category">Storage</div>
          <div class="tech-name">${e(techStack.storage)}</div>
        </div>
        ` : ''}

        ${techStack.cache ? `
        <div class="tech-item">
          <div class="tech-category">Cache</div>
          <div class="tech-name">${e(techStack.cache)}</div>
        </div>
        ` : ''}

        ${techStack.queue ? `
        <div class="tech-item">
          <div class="tech-category">Queue</div>
          <div class="tech-name">${e(techStack.queue)}</div>
        </div>
        ` : ''}
      </div>

      ${techStack.additionalServices?.length > 0 ? `
      <div class="subsection">
        <div class="subsection-title">Additional Services</div>
        ${renderList(techStack.additionalServices)}
      </div>
      ` : ''}

      ${research?.estimatedCost ? `
      <div class="subsection">
        <div class="subsection-title">Cost Breakdown</div>
        <div class="card">
          ${formatComplexData(research.estimatedCost)}
        </div>
      </div>
      ` : ''}

      ${Object.keys(serviceRecommendations).length > 0 ? `
      <div class="subsection">
        <div class="subsection-title">Service Recommendations</div>
        <div class="card">${formatComplexData(serviceRecommendations)}</div>
      </div>
      ` : ''}

      ${Object.keys(budgetAnalysis).length > 0 ? `
      <div class="subsection">
        <div class="subsection-title">Budget Analysis</div>
        <div class="card">${formatComplexData(budgetAnalysis)}</div>
      </div>
      ` : ''}

      ${Object.keys(freeTierAnalysis).length > 0 ? `
      <div class="subsection">
        <div class="subsection-title">Free Tier Analysis</div>
        <div class="card">${formatComplexData(freeTierAnalysis)}</div>
      </div>
      ` : ''}
    </div>

    <!-- Technical Architecture -->
    ${Object.keys(techArch).length > 0 || Object.keys(blueprint).length > 0 ? `
    <div class="section">
      <h2 class="section-title"><span class="icon">🏗️</span> Technical Architecture</h2>

      ${techArch.frontendArchitecture ? `
      <div class="subsection">
        <div class="subsection-title">Frontend Architecture</div>
        <div class="card">${formatComplexData(techArch.frontendArchitecture)}</div>
      </div>
      ` : ''}

      ${techArch.backendArchitecture ? `
      <div class="subsection">
        <div class="subsection-title">Backend Architecture</div>
        <div class="card">${formatComplexData(techArch.backendArchitecture)}</div>
      </div>
      ` : ''}

      ${techArch.databaseArchitecture ? `
      <div class="subsection">
        <div class="subsection-title">Database Architecture</div>
        <div class="card">${formatComplexData(techArch.databaseArchitecture)}</div>
      </div>
      ` : ''}

      ${techArch.securityArchitecture ? `
      <div class="subsection">
        <div class="subsection-title">Security Architecture</div>
        <div class="card">${formatComplexData(techArch.securityArchitecture)}</div>
      </div>
      ` : ''}

      ${techArch.integrationArchitecture ? `
      <div class="subsection">
        <div class="subsection-title">Integration Architecture</div>
        <div class="card">${formatComplexData(techArch.integrationArchitecture)}</div>
      </div>
      ` : ''}

      ${techArch.performanceArchitecture ? `
      <div class="subsection">
        <div class="subsection-title">Performance Architecture</div>
        <div class="card">${formatComplexData(techArch.performanceArchitecture)}</div>
      </div>
      ` : ''}

      ${blueprint.crossCuttingConcerns ? `
      <div class="subsection">
        <div class="subsection-title">Cross-Cutting Concerns</div>
        <div class="card">${formatComplexData(blueprint.crossCuttingConcerns)}</div>
      </div>
      ` : ''}

      ${blueprint.presentationLayer || blueprint.businessLayer || blueprint.persistenceLayer || blueprint.frontendLayer || blueprint.backendLayer || blueprint.deploymentLayer || blueprint.databaseLayer || blueprint.securityLayer ? `
      <div class="subsection">
        <div class="subsection-title">Architecture Layers</div>
        ${blueprint.presentationLayer || blueprint.frontendLayer ? `<div class="card"><div class="card-title">Presentation / Frontend Layer</div><div class="card-content">${formatComplexData(blueprint.presentationLayer || blueprint.frontendLayer)}</div></div>` : ''}
        ${blueprint.businessLayer || blueprint.backendLayer ? `<div class="card"><div class="card-title">Business / Backend Layer</div><div class="card-content">${formatComplexData(blueprint.businessLayer || blueprint.backendLayer)}</div></div>` : ''}
        ${blueprint.persistenceLayer || blueprint.databaseLayer ? `<div class="card"><div class="card-title">Data Access / Database Layer</div><div class="card-content">${formatComplexData(blueprint.persistenceLayer || blueprint.databaseLayer)}</div></div>` : ''}
        ${blueprint.securityLayer ? `<div class="card"><div class="card-title">Security Layer</div><div class="card-content">${formatComplexData(blueprint.securityLayer)}</div></div>` : ''}
        ${blueprint.deploymentLayer ? `<div class="card"><div class="card-title">Deployment Layer</div><div class="card-content">${formatComplexData(blueprint.deploymentLayer)}</div></div>` : ''}
      </div>
      ` : ''}

      ${blueprint.projectStructure ? `
      <div class="subsection">
        <div class="subsection-title">Project Structure</div>
        <div class="card">${formatComplexData(blueprint.projectStructure)}</div>
      </div>
      ` : ''}

      ${refinedHLD.developmentStrategy ? `
      <div class="subsection">
        <div class="subsection-title">Development Strategy</div>
        <div class="card">${formatComplexData(refinedHLD.developmentStrategy)}</div>
      </div>
      ` : ''}
    </div>
    ` : ''}

    <!-- Research & Validation -->
    ${(research || validation) ? `
    <div class="section">
      <h2 class="section-title"><span class="icon">🔬</span> Research & Validation ${validation ? `<span class="badge ${isArchitectureValid ? '' : 'muted'}" style="font-size: 12px; margin-left: 12px;">${isArchitectureValid ? '✓ Valid' : '⚠ Needs Review'}</span>` : ''}</h2>

      ${research?.marketAnalysis ? `
      <div class="subsection">
        <div class="subsection-title">Market Analysis</div>
        <div class="card">${typeof research.marketAnalysis === 'string' ? `<p>${e(research.marketAnalysis)}</p>` : formatComplexData(research.marketAnalysis)}</div>
      </div>
      ` : ''}

      ${research?.competitorAnalysis ? `
      <div class="subsection">
        <div class="subsection-title">Competitor Analysis</div>
        <div class="card">${typeof research.competitorAnalysis === 'string' ? `<p>${e(research.competitorAnalysis)}</p>` : formatComplexData(research.competitorAnalysis)}</div>
      </div>
      ` : ''}

      ${research?.reasoning ? `
      <div class="subsection">
        <div class="subsection-title">Architecture Reasoning</div>
        <div class="card"><div class="card-content">${e(research.reasoning)}</div></div>
      </div>
      ` : ''}

      ${research?.researchSources && (Array.isArray(research.researchSources) || typeof research.researchSources === 'object') ? `
      <div class="subsection">
        <div class="subsection-title">Research Sources</div>
        <div class="card">${formatComplexData(research.researchSources)}</div>
      </div>
      ` : ''}

      ${research?.researchedServices ? `
      <div class="subsection">
        <div class="subsection-title">Researched Services</div>
        <div class="card">${formatComplexData(research.researchedServices)}</div>
      </div>
      ` : ''}

      ${improvements.length > 0 ? `
      <div class="subsection">
        <div class="subsection-title">Suggested Improvements</div>
        ${renderList(improvements)}
      </div>
      ` : ''}

      ${concerns.length > 0 ? `
      <div class="subsection">
        <div class="subsection-title">Concerns</div>
        ${renderList(concerns)}
      </div>
      ` : ''}

      ${betterAlternatives.length > 0 ? `
      <div class="subsection">
        <div class="subsection-title">Better Free Alternatives</div>
        ${betterAlternatives.map(alt => {
          if (typeof alt === 'string') return `<div class="card"><div class="card-content">${e(alt)}</div></div>`;
          const insteadOf = alt['instead-of'] || alt.insteadOf || alt.current || '';
          const use = alt.use || alt.alternative || alt.recommendation || '';
          const reason = alt.reason || alt.why || alt.benefit || '';
          return `
          <div class="card">
            ${insteadOf ? `<div class="card-title" style="color: #f87171;">Instead of: ${e(insteadOf)}</div>` : ''}
            ${use ? `<div style="color: #4ade80; font-weight: 600; margin: 8px 0;">Use: ${e(use)}</div>` : ''}
            ${reason ? `<div class="card-content">${e(reason)}</div>` : ''}
          </div>
          `;
        }).join('')}
      </div>
      ` : ''}

      ${validationRecommendations ? `
      <div class="subsection">
        <div class="subsection-title">Validation Recommendations</div>
        <div class="card"><div class="card-content">${e(validationRecommendations)}</div></div>
      </div>
      ` : ''}

      ${Object.keys(validationResults).length > 0 ? `
      <div class="subsection">
        <div class="subsection-title">Validation Results</div>
        <table>
          <thead><tr><th>Criterion</th><th>Result</th></tr></thead>
          <tbody>
            ${Object.entries(validationResults).map(([key, val]) => `
              <tr><td>${e(key.replace(/([A-Z])/g, ' $1').trim())}</td><td>${e(typeof val === 'object' ? JSON.stringify(val) : String(val))}</td></tr>
            `).join('')}
          </tbody>
        </table>
      </div>
      ` : ''}

      ${refinedArch && Object.keys(refinedArch).filter(k => k !== 'techStack').length > 0 ? `
      <div class="subsection">
        <div class="subsection-title">Refined Architecture Recommendations</div>
        <div class="card">${formatComplexData(Object.fromEntries(Object.entries(refinedArch).filter(([k]) => k !== 'techStack')))}</div>
      </div>
      ` : ''}

      ${improvedArch && Object.keys(improvedArch).filter(k => k !== 'techStack').length > 0 ? `
      <div class="subsection">
        <div class="subsection-title">Improved Architecture</div>
        <div class="card">${formatComplexData(Object.fromEntries(Object.entries(improvedArch).filter(([k]) => k !== 'techStack')))}</div>
      </div>
      ` : ''}
    </div>
    ` : ''}

    <!-- Risk Assessment -->
    ${(technicalRisks.length > 0 || businessRisks.length > 0 || timelineRisks.length > 0) ? `
    <div class="section">
      <h2 class="section-title"><span class="icon">⚠️</span> Risk Assessment</h2>

      ${technicalRisks.length > 0 ? `
      <div class="subsection">
        <div class="subsection-title">Technical Risks</div>
        ${technicalRisks.map(risk => {
          const name = typeof risk === 'string' ? risk : (risk?.risk || risk?.name || 'Risk');
          const prob = risk?.probability || '';
          const impact = risk?.impact || '';
          const mitigation = risk?.mitigation || '';
          return `
            <div class="card">
              <div class="card-title">${e(name)}</div>
              <div class="card-content">
                ${prob ? `<strong>Probability:</strong> <span class="risk-${prob.toLowerCase().includes('high') ? 'high' : prob.toLowerCase().includes('medium') ? 'medium' : 'low'}">${e(prob)}</span><br>` : ''}
                ${impact ? `<strong>Impact:</strong> ${e(impact)}<br>` : ''}
                ${mitigation ? `<strong>Mitigation:</strong> ${e(mitigation)}` : ''}
              </div>
            </div>
          `;
        }).join('')}
      </div>
      ` : ''}

      ${businessRisks.length > 0 ? `
      <div class="subsection">
        <div class="subsection-title">Business Risks</div>
        ${renderList(businessRisks)}
      </div>
      ` : ''}

      ${timelineRisks.length > 0 ? `
      <div class="subsection">
        <div class="subsection-title">Timeline Risks</div>
        ${timelineRisks.map(risk => {
          const name = typeof risk === 'string' ? risk : (risk?.risk || risk?.name || 'Risk');
          const prob = risk?.probability || '';
          const impact = risk?.impact || '';
          const mitigation = risk?.mitigation || '';
          return `
            <div class="card">
              <div class="card-title">${e(name)}</div>
              <div class="card-content">
                ${prob ? `<strong>Probability:</strong> <span class="risk-${prob.toLowerCase().includes('high') ? 'high' : prob.toLowerCase().includes('medium') ? 'medium' : 'low'}">${e(prob)}</span><br>` : ''}
                ${impact ? `<strong>Impact:</strong> ${e(impact)}<br>` : ''}
                ${mitigation ? `<strong>Mitigation:</strong> ${e(mitigation)}` : ''}
              </div>
            </div>
          `;
        }).join('')}
      </div>
      ` : ''}

      ${mitigations.length > 0 ? `
      <div class="subsection">
        <div class="subsection-title">Mitigation Strategies</div>
        ${renderList(mitigations)}
      </div>
      ` : ''}
    </div>
    ` : ''}

    <!-- Flow Diagrams -->
    ${(diagrams.userJourney || diagrams.systemFlow || diagrams.authFlow || diagrams.systemArchitecture) ? `
    <div class="section diagrams-section">
      <h2 class="section-title"><span class="icon">📐</span> Application Flow Diagrams</h2>

      ${diagrams.userJourney ? `
      <div class="mermaid-diagram">
        <div class="diagram-label">User Journey</div>
        <pre class="mermaid">${diagrams.userJourney}</pre>
      </div>
      ` : ''}

      ${diagrams.systemFlow ? `
      <div class="mermaid-diagram">
        <div class="diagram-label">System Flow</div>
        <pre class="mermaid">${diagrams.systemFlow}</pre>
      </div>
      ` : ''}

      ${diagrams.authFlow ? `
      <div class="mermaid-diagram">
        <div class="diagram-label">Authentication Flow</div>
        <pre class="mermaid">${diagrams.authFlow}</pre>
      </div>
      ` : ''}

      ${diagrams.systemArchitecture ? `
      <div class="mermaid-diagram">
        <div class="diagram-label">System Architecture</div>
        <pre class="mermaid">${diagrams.systemArchitecture}</pre>
      </div>
      ` : ''}

      ${diagrams.deploymentPipeline ? `
      <div class="mermaid-diagram">
        <div class="diagram-label">Deployment Pipeline</div>
        <pre class="mermaid">${diagrams.deploymentPipeline}</pre>
      </div>
      ` : ''}

      ${/* Catch any additional diagrams not explicitly handled */ ''}
      ${Object.entries(diagrams).filter(([key]) =>
        !['userJourney', 'systemFlow', 'authFlow', 'systemArchitecture', 'deploymentPipeline', 'data'].includes(key) &&
        typeof diagrams[key] === 'string' && diagrams[key].trim()
      ).map(([key, diagram]) => `
      <div class="mermaid-diagram">
        <div class="diagram-label">${e(key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()))}</div>
        <pre class="mermaid">${diagram}</pre>
      </div>
      `).join('')}
    </div>
    ` : ''}

    <!-- AI Prompts -->
    ${promptsList.length > 0 ? `
    <div class="section">
      <h2 class="section-title"><span class="icon">🚀</span> AI Implementation Prompts</h2>
      <p style="color: rgba(255,255,255,0.6); margin-bottom: 20px; font-size: 13px;">${promptsList.length} prompts generated for ${e(platform || 'your platform')}. Use these with your AI coding assistant.</p>

      ${totalEstimatedTime || criticalPath.length > 0 ? `
      <div class="stats" style="margin-bottom: 24px;">
        ${totalEstimatedTime ? `<div class="stat"><div class="stat-value">${e(totalEstimatedTime)}</div><div class="stat-label">Total Est. Time</div></div>` : ''}
        ${criticalPath.length > 0 ? `<div class="stat"><div class="stat-value">${criticalPath.length}</div><div class="stat-label">Critical Path Steps</div></div>` : ''}
        <div class="stat"><div class="stat-value">${promptsList.length}</div><div class="stat-label">Total Prompts</div></div>
      </div>
      ` : ''}

      ${promptsList.map((prompt, idx) => {
        const title = prompt.title || prompt.name || `Step ${idx + 1}`;
        const content = prompt.prompt || prompt.content || prompt.description || prompt.cursorPrompt?.command || '';
        const time = prompt.estimatedTime || prompt.time || '';
        const difficulty = prompt.difficulty || prompt.estimatedComplexity || '';
        const category = prompt.category || prompt.phase || '';
        const goal = prompt.goal || prompt.objective || '';
        const deliverables = prompt.deliverables || prompt.expectedOutput || [];
        const explanation = prompt.explanation || '';
        const aftercare = prompt.aftercare || [];
        const validationCriteria = prompt.validationCriteria || [];

        return `
        <div class="prompt">
          <div class="prompt-header">
            <span class="prompt-number">#${idx + 1}</span>
            <span class="prompt-meta">${[time, difficulty, category].filter(Boolean).join(' • ')}</span>
          </div>
          <div class="prompt-title">${e(title)}</div>
          ${goal ? `<div class="prompt-goal"><strong>Goal:</strong> ${e(goal)}</div>` : ''}
          <div class="prompt-content">${e(content)}</div>
          ${explanation ? `
          <div class="prompt-deliverables">
            <strong>Why This Matters:</strong>
            <p style="margin: 8px 0; color: rgba(255,255,255,0.7);">${e(explanation)}</p>
          </div>
          ` : ''}
          ${Array.isArray(deliverables) && deliverables.length > 0 ? `
          <div class="prompt-deliverables">
            <strong>Deliverables:</strong>
            <ul>${deliverables.slice(0, 6).map(d => `<li>${e(typeof d === 'string' ? d : d?.name || d?.description || '')}</li>`).join('')}</ul>
          </div>
          ` : ''}
          ${Array.isArray(aftercare) && aftercare.length > 0 ? `
          <div class="prompt-deliverables">
            <strong>After Completion:</strong>
            <ul>${aftercare.map(step => `<li>${e(step)}</li>`).join('')}</ul>
          </div>
          ` : ''}
          ${Array.isArray(validationCriteria) && validationCriteria.length > 0 ? `
          <div class="prompt-deliverables">
            <strong>Validation Checklist:</strong>
            <ul>${validationCriteria.map(criteria => `<li>☐ ${e(criteria)}</li>`).join('')}</ul>
          </div>
          ` : ''}
        </div>
        `;
      }).join('')}
    </div>
    ` : ''}

    <!-- Footer -->
    <div class="footer">
      <p>Generated by <a href="https://theaiarchitect.ai">The AI Architect</a> on ${date}</p>
      <p class="no-print" style="margin-top: 12px;">Press <strong>Ctrl+P</strong> (Cmd+P on Mac) to save as PDF</p>
    </div>
  </div>

  <script>
    // Initialize Mermaid with dark theme matching the app
    mermaid.initialize({
      startOnLoad: true,
      theme: 'dark',
      securityLevel: 'loose',
      fontFamily: 'inherit',
      themeVariables: {
        darkMode: true,
        primaryColor: '#7B61FF',
        primaryTextColor: '#E5E7EB',
        primaryBorderColor: '#3A3D46',
        lineColor: '#7B61FF',
        secondaryColor: '#141722',
        tertiaryColor: '#0F1117',
        background: '#0F1117',
        mainBkg: '#141722',
        secondBkg: '#0F1117',
        tertiaryTextColor: '#E5E7EB'
      },
      flowchart: {
        useMaxWidth: true,
        htmlLabels: true,
        curve: 'linear'
      },
      sequence: {
        useMaxWidth: true,
        wrap: true
      },
      journey: {
        useMaxWidth: true,
        rightAngles: false
      }
    });
  </script>
</body>
</html>`;
}

export default ExportHTMLButton;
